import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Edit,
  Target,
  DollarSign,
  Users,
  TrendingUp,
  BarChart3,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  Calendar,
  Activity
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import leadSourceMasterService from '../../services/leadSourceMasterService'

const LeadSourceMasterView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState('leadSourceInfo')
  const [loading, setLoading] = useState(true)
  const [leadSource, setLeadSource] = useState(null)

  useEffect(() => {
    loadLeadSource()
  }, [id])

  const loadLeadSource = async () => {
    try {
      setLoading(true)
      const response = await leadSourceMasterService.getLeadSourceById(id)
      
      if (response.success) {
        setLeadSource(response.data)
      } else {
        toast.error(response.message || 'Failed to load lead source')
        navigate('/crm/lead-source-master')
      }
    } catch (error) {
      console.error('Error loading lead source:', error)
      toast.error('Failed to load lead source')
      navigate('/crm/lead-source-master')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    navigate(`/crm/lead-source-master/edit/${id}`)
  }

  const handleBack = () => {
    navigate('/crm/lead-source-master')
  }

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Source Master',
      path: '/crm/lead-source-master',
      onClick: () => navigate('/crm/lead-source-master')
    },
    {
      label: 'View Lead Source',
      path: `/crm/lead-source-master/view/${id}`,
      isCurrent: true
    }
  ]

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading lead source...</p>
        </div>
      </div>
    )
  }

  if (!leadSource) {
    return (
      <div className="sales-dashboard">
        <div className="error-container">
          <p>Lead source not found</p>
          <button onClick={handleBack} className="btn-primary">
            Back to Lead Sources
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>View Lead Source</h1>
          <p>Lead source details and performance metrics</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-secondary"
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            Back to Lead Sources
          </button>
          <button 
            className="btn-primary"
            onClick={handleEdit}
          >
            <Edit size={16} />
            Edit Lead Source
          </button>
        </div>
      </div>

      {/* Lead Source Overview */}
      <div className="overview-cards">
        <div className="overview-card">
          <div className="overview-card-header">
            <Target size={20} className="text-blue-500" />
            <h3>Lead Source Information</h3>
          </div>
          <div className="overview-card-content">
            <div className="info-row">
              <span className="info-label">Code:</span>
              <span className="info-value">{leadSource.leadSourceCode}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Name:</span>
              <span className="info-value">{leadSource.leadSourceName}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Category:</span>
              <span className="info-value">{leadSource.leadSourceCategory}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Type:</span>
              <span className="info-value">{leadSource.leadSourceType}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className={`status-badge ${leadSource.leadSourceStatus.toLowerCase()}`}>
                {leadSource.leadSourceStatus}
              </span>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <div className="overview-card-header">
            <BarChart3 size={20} className="text-green-500" />
            <h3>Performance Metrics</h3>
          </div>
          <div className="overview-card-content">
            <div className="info-row">
              <span className="info-label">Leads Generated:</span>
              <span className="info-value">{leadSource.totalLeadsGenerated || 0}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Leads Converted:</span>
              <span className="info-value">{leadSource.totalLeadsConverted || 0}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Conversion Rate:</span>
              <span className="info-value">
                {leadSource.totalLeadsGenerated > 0 
                  ? ((leadSource.totalLeadsConverted / leadSource.totalLeadsGenerated) * 100).toFixed(2) + '%'
                  : '0%'
                }
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Total Revenue:</span>
              <span className="info-value">₹{leadSource.totalRevenue || 0}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Cost Per Lead:</span>
              <span className="info-value">₹{leadSource.costPerLead || 0}</span>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <div className="overview-card-header">
            <Users size={20} className="text-purple-500" />
            <h3>Quality Scores</h3>
          </div>
          <div className="overview-card-content">
            <div className="score-item">
              <span className="score-label">Lead Quality:</span>
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{ width: `${(leadSource.leadQualityScore / 10) * 100}%` }}
                ></div>
              </div>
              <span className="score-value">{leadSource.leadQualityScore}/10</span>
            </div>
            <div className="score-item">
              <span className="score-label">Volume Score:</span>
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{ width: `${(leadSource.leadVolumeScore / 10) * 100}%` }}
                ></div>
              </div>
              <span className="score-value">{leadSource.leadVolumeScore}/10</span>
            </div>
            <div className="score-item">
              <span className="score-label">Cost Effectiveness:</span>
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{ width: `${(leadSource.costEffectivenessScore / 10) * 100}%` }}
                ></div>
              </div>
              <span className="score-value">{leadSource.costEffectivenessScore}/10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'leadSourceInfo' ? 'active' : ''}`}
          onClick={() => setActiveTab('leadSourceInfo')}
        >
          <Target size={18} />
          <span>Lead Source Info</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <BarChart3 size={18} />
          <span>Performance Metrics</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          <Globe size={18} />
          <span>Tracking & Integration</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'targeting' ? 'active' : ''}`}
          onClick={() => setActiveTab('targeting')}
        >
          <Users size={18} />
          <span>Targeting & Demographics</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="form-container">
        <div className="form-content">
          {activeTab === 'leadSourceInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Lead Source Information</h3>
              </div>
              
              <div className="view-grid">
                <div className="view-group">
                  <label className="view-label">Lead Source Code</label>
                  <div className="view-value">{leadSource.leadSourceCode}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Lead Source Name</label>
                  <div className="view-value">{leadSource.leadSourceName}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Category</label>
                  <div className="view-value">{leadSource.leadSourceCategory}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Type</label>
                  <div className="view-value">{leadSource.leadSourceType}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Status</label>
                  <div className="view-value">
                    <span className={`status-badge ${leadSource.leadSourceStatus.toLowerCase()}`}>
                      {leadSource.leadSourceStatus}
                    </span>
                  </div>
                </div>
                <div className="view-group">
                  <label className="view-label">Contact Person</label>
                  <div className="view-value">{leadSource.contactPerson || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Contact Email</label>
                  <div className="view-value">{leadSource.contactEmail || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Contact Phone</label>
                  <div className="view-value">{leadSource.contactPhone || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Website</label>
                  <div className="view-value">
                    {leadSource.website ? (
                      <a href={leadSource.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {leadSource.website}
                      </a>
                    ) : 'N/A'}
                  </div>
                </div>
                <div className="view-group full-width">
                  <label className="view-label">Description</label>
                  <div className="view-value">{leadSource.leadSourceDescription || 'N/A'}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Performance Metrics</h3>
              </div>
              
              <div className="view-grid">
                <div className="view-group">
                  <label className="view-label">Total Leads Generated</label>
                  <div className="view-value">{leadSource.totalLeadsGenerated || 0}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Total Leads Converted</label>
                  <div className="view-value">{leadSource.totalLeadsConverted || 0}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Conversion Rate</label>
                  <div className="view-value">
                    {leadSource.totalLeadsGenerated > 0 
                      ? ((leadSource.totalLeadsConverted / leadSource.totalLeadsGenerated) * 100).toFixed(2) + '%'
                      : '0%'
                    }
                  </div>
                </div>
                <div className="view-group">
                  <label className="view-label">Total Revenue</label>
                  <div className="view-value">₹{leadSource.totalRevenue || 0}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Cost Per Lead</label>
                  <div className="view-value">₹{leadSource.costPerLead || 0}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Expected Conversion Rate</label>
                  <div className="view-value">{leadSource.expectedConversionRate || 0}%</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Average Deal Value</label>
                  <div className="view-value">₹{leadSource.averageDealValue || 0}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Last Lead Date</label>
                  <div className="view-value">
                    {leadSource.lastLeadDate ? new Date(leadSource.lastLeadDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tracking' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Tracking & Integration</h3>
              </div>
              
              <div className="view-grid">
                <div className="view-group">
                  <label className="view-label">Tracking Code</label>
                  <div className="view-value">{leadSource.trackingCode || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">UTM Source</label>
                  <div className="view-value">{leadSource.utmSource || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">UTM Medium</label>
                  <div className="view-value">{leadSource.utmMedium || 'N/A'}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">UTM Campaign</label>
                  <div className="view-value">{leadSource.utmCampaign || 'N/A'}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'targeting' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Targeting & Demographics</h3>
              </div>
              
              <div className="view-grid">
                <div className="view-group">
                  <label className="view-label">Target Company Size</label>
                  <div className="view-value">{leadSource.targetCompanySize}</div>
                </div>
                <div className="view-group">
                  <label className="view-label">Target Regions</label>
                  <div className="view-value">
                    {leadSource.targetRegions?.length > 0 ? leadSource.targetRegions.join(', ') : 'N/A'}
                  </div>
                </div>
                <div className="view-group">
                  <label className="view-label">Target Industries</label>
                  <div className="view-value">
                    {leadSource.targetIndustries?.length > 0 ? leadSource.targetIndustries.join(', ') : 'N/A'}
                  </div>
                </div>
                <div className="view-group">
                  <label className="view-label">Tags</label>
                  <div className="view-value">
                    {leadSource.tags?.length > 0 ? (
                      <div className="tags-container">
                        {leadSource.tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    ) : 'N/A'}
                  </div>
                </div>
                <div className="view-group full-width">
                  <label className="view-label">Notes</label>
                  <div className="view-value">{leadSource.notes || 'N/A'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LeadSourceMasterView
